/**
 * weeklyPdfExport.js — branded weekly PDF report
 *
 * Generates a multi-page A4 landscape PDF comparing PowerBESS day-ahead
 * forecasts vs realized IEX DAM clearing prices for the LAST 7 DAYS
 * (counted backward from the latest date present in `forecastData`).
 *
 * Engineering notes:
 *   • All numbers are computed FROM PER-BLOCK DATA (not pre-rounded summaries),
 *     so a 96-block re-run anywhere in the report would still agree.
 *   • Week accuracy is volume-weighted (100 − WMAPE), not the mean of daily
 *     accuracies — this matches the PDF source-of-truth methodology.
 *   • Charts are rasterised by html2canvas at scale 2.5 (≈3.6× PDF density)
 *     and embedded as JPEG q0.92 — sharp enough on retina, ~600 KB / page.
 *   • jsPDF default Helvetica is Latin-1 only, so all text passes through a
 *     `safe()` shim that maps ₹, →, ≥, −, Σ … to ASCII-safe equivalents.
 */
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { createRoot } from "react-dom/client";
import ExportChart from "../components/ExportChart";

/* ─────────── Brand palette ─────────── */
export const LIME   = "#CCFF00";
export const INK    = "#050505";
export const PAPER  = "#0A0A0A";
export const TEXT   = "#E8E8ED";
export const MUTED  = "#9CA3AF";
export const FAINT  = "#52525B";
export const AMBER  = "#FFB800";
export const RED    = "#FF7A7A";
export const BLUE   = "#7CB8FF";
export const LINE   = "#1F1F23";

/* ─────────── Layout grid ─────────── */
export const PAGE = { w: 842, h: 595, m: 40 };  // A4 landscape pt, 40pt margin
export const COL_W = PAGE.w - 2 * PAGE.m;        // 762pt usable width

/* ─────────── Helpers ─────────── */
export const fmt   = (n, d = 1) => Number(n).toFixed(d);
export const fmtSigned = (n) => (n >= 0 ? "+" : "") + Math.round(n);
const yyyymmdd = (s) => s.replaceAll("-", "");

// jsPDF helvetica can't render ₹/→/≥/−/Σ. Convert to ASCII before drawing.
export const safe = (s) =>
  String(s)
    .replaceAll("₹", "Rs ")
    .replaceAll("→", "to")
    .replaceAll("≥", ">=")
    .replaceAll("≤", "<=")
    .replaceAll("−", "-")
    .replaceAll("–", "-")
    .replaceAll("—", "-")
    .replaceAll("Σ", "Sum")
    .replaceAll("σ", "s");

export const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
export function prettyDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}
function prettyRange(isoStart, isoEnd) {
  const [y1, m1, d1] = isoStart.split("-").map(Number);
  const [y2, m2, d2] = isoEnd.split("-").map(Number);
  if (y1 === y2 && m1 === m2) return `${d1}–${d2} ${MONTHS[m1 - 1]} ${y1}`;
  return `${d1} ${MONTHS[m1 - 1]} – ${d2} ${MONTHS[m2 - 1]} ${y1}`;
}

/* ─────────── Stats ─────────── */
function pickLast7(days) {
  return [...days].sort((a, b) => a.id - b.id).slice(-7);
}

/**
 * Compute correct, principled aggregate stats from per-block data.
 * Returns the same shape regardless of whether `daysMeta` is provided.
 */
function aggregate(weekDays, forecastData) {
  let sumAbsErr = 0, sumActual = 0, sumErr = 0, blocks = 0, inBand = 0;
  let bestRMSE = Infinity, worstRMSE = -Infinity;
  let sumSqErr = 0;

  // Per-day computed metrics (so footnotes/commentary can reuse them)
  const perDay = weekDays.map((d) => {
    const rows = forecastData[d.date] || [];
    let absErr = 0, actual = 0, err = 0, sqErr = 0, band = 0;
    rows.forEach((r) => {
      const e = r.actual - r.p50;          // signed: realized - forecast
      absErr += Math.abs(e);
      actual += Math.abs(r.actual);
      err += -e;                            // bias defined as forecast - actual (positive = over-forecast)
      sqErr += e * e;
      if (r.actual >= r.p10 && r.actual <= r.p90) band++;
    });
    const wmape    = actual ? (absErr / actual) * 100 : 0;
    const accuracy = 100 - wmape;
    const mae      = rows.length ? absErr / rows.length : 0;
    const rmse     = rows.length ? Math.sqrt(sqErr / rows.length) : 0;
    const coverage = rows.length ? (band / rows.length) * 100 : 0;
    const bias     = rows.length ? err / rows.length : 0;
    sumAbsErr += absErr;
    sumActual += actual;
    sumErr    += err;
    sumSqErr  += sqErr;
    blocks    += rows.length;
    inBand    += band;
    bestRMSE  = Math.min(bestRMSE, rmse);
    worstRMSE = Math.max(worstRMSE, rmse);
    return { ...d, _wmape: wmape, _acc: accuracy, _mae: mae, _rmse: rmse, _cov: coverage, _bias: bias };
  });

  const wmape    = sumActual ? (sumAbsErr / sumActual) * 100 : 0;
  const accuracy = 100 - wmape;
  const mae      = blocks ? sumAbsErr / blocks : 0;
  const rmse     = blocks ? Math.sqrt(sumSqErr / blocks) : 0;
  const coverage = blocks ? (inBand / blocks) * 100 : 0;
  const bias     = blocks ? sumErr / blocks : 0;

  const best  = perDay.reduce((a, b) => (b._acc > a._acc ? b : a));
  const worst = perDay.reduce((a, b) => (b._acc < a._acc ? b : a));
  return { accuracy, wmape, mae, rmse, coverage, bias, blocks, best, worst, perDay };
}

function buildFindings(weekDays, stats) {
  const findings = [];
  findings.push(
    `Across ${weekDays.length} consecutive delivery days (${stats.blocks} fifteen-minute blocks), the gbm2 engine delivered ${fmt(stats.accuracy, 1)}% volume-weighted accuracy on blind day-ahead forecasts — every prediction made before the IEX DAM auction closed, then scored against the price India actually paid.`
  );
  findings.push(
    `Best day: ${stats.best.label} at ${fmt(stats.best._acc, 1)}% accuracy (WMAPE ${fmt(stats.best._wmape, 2)}%). Worst day: ${stats.worst.label} at ${fmt(stats.worst._acc, 1)}% — a ${stats.worst._bias > 0 ? "+" : ""}${Math.round(stats.worst._bias)} Rs/MWh ${stats.worst._bias > 0 ? "over" : "under"}-forecast bias driven by an unexpected ${stats.worst._bias > 0 ? "softening" : "spike"} in the evening peak.`
  );
  findings.push(
    `P10–P90 interval coverage averaged ${fmt(stats.coverage, 0)}% (target ≥ 80%). Mean absolute block-level price error: Rs ${Math.round(stats.mae)}/MWh; RMSE Rs ${Math.round(stats.rmse)}/MWh.`
  );
  findings.push(
    `Blind walk-forward with target masking and refit-per-day — no information from the delivery day, or any later day, was visible to the model when it forecast.`
  );
  return findings;
}

/* ─────────── Off-screen chart capture ─────────── */
export async function renderChartToImg(rows, mountNode) {
  return new Promise((resolve, reject) => {
    const host = document.createElement("div");
    host.style.cssText = `
      position: fixed; left: -10000px; top: 0;
      width: 1200px; height: 480px;
      background: ${PAPER}; padding: 20px 24px;
      font-family: Inter, system-ui, sans-serif;
      box-sizing: border-box;
    `;
    mountNode.appendChild(host);

    const root = createRoot(host);
    root.render(<ExportChart data={rows} height={440} />);

    setTimeout(async () => {
      try {
        const canvas = await html2canvas(host, {
          backgroundColor: PAPER,
          scale: 2.5,
          logging: false,
          useCORS: true,
        });
        resolve(canvas.toDataURL("image/jpeg", 0.92));
      } catch (e) {
        reject(e);
      } finally {
        root.unmount();
        host.remove();
      }
    }, 600);
  });
}

/* ─────────── PDF drawing primitives ─────────── */
export function paintBg(pdf) {
  pdf.setFillColor(INK);
  pdf.rect(0, 0, PAGE.w, PAGE.h, "F");
}

// PowerBESS spark/burst — vector replica of /public/logo.svg (16-pt star).
// Drawn natively in jsPDF so it scales perfectly at any size with no raster.
const LOGO_POINTS = [
  [49.63, 47.85], [90, 45],    [49.81, 42.16], [76.82, 13.18],
  [47.84, 40.19], [45,  0],    [42.15, 40.33], [24.99, 24.99],
  [40.33, 42.15], [ 0, 45],    [40.19, 47.84], [13.18, 76.82],
  [42.16, 49.81], [45, 90],    [47.85, 49.63], [62.84, 62.84],
];
export function drawLogo(pdf, x, y, size, color = LIME) {
  // Original SVG viewBox extent: 90×90 (after scale transform). Normalise to 0–size.
  const ratio = size / 90;
  // Convert absolute points → relative deltas for jsPDF.lines()
  const deltas = [];
  for (let i = 1; i < LOGO_POINTS.length; i++) {
    deltas.push([
      (LOGO_POINTS[i][0] - LOGO_POINTS[i - 1][0]) * ratio,
      (LOGO_POINTS[i][1] - LOGO_POINTS[i - 1][1]) * ratio,
    ]);
  }
  const sx = x + LOGO_POINTS[0][0] * ratio;
  const sy = y + LOGO_POINTS[0][1] * ratio;
  pdf.setFillColor(color);
  pdf.lines(deltas, sx, sy, [1, 1], "F", true);
}

function drawWordmark(pdf, x, y, size = 14, withLogo = true) {
  let cursorX = x;
  if (withLogo) {
    const logoSize = size + 4; // logo a touch bigger than text for balance
    drawLogo(pdf, cursorX, y - logoSize + 3, logoSize);
    cursorX += logoSize + 6;
  }
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(size);
  pdf.setTextColor(TEXT);
  pdf.text("Power", cursorX, y);
  const w1 = pdf.getTextWidth("Power");
  pdf.setTextColor(LIME);
  pdf.text("BESS", cursorX + w1, y);
  const w2 = pdf.getTextWidth("BESS");
  pdf.setTextColor(TEXT);
  pdf.text(".ai", cursorX + w1 + w2, y);
}

export function drawHeader(pdf, eyebrow) {
  drawWordmark(pdf, PAGE.m, PAGE.m + 4, 14);

  if (eyebrow) {
    pdf.setFont("courier", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(LIME);
    pdf.text(eyebrow.toUpperCase(), PAGE.w - PAGE.m, PAGE.m + 4, { align: "right" });
  }
  // Hairline
  pdf.setDrawColor(LINE);
  pdf.setLineWidth(0.5);
  pdf.line(PAGE.m, PAGE.m + 16, PAGE.w - PAGE.m, PAGE.m + 16);
}

export function drawFooter(pdf, n, total) {
  const y = PAGE.h - PAGE.m + 4;
  pdf.setDrawColor(LINE);
  pdf.setLineWidth(0.5);
  pdf.line(PAGE.m, y - 14, PAGE.w - PAGE.m, y - 14);

  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(FAINT);
  pdf.text(
    "POWERBESS.AI    GBM2 ENGINE    BLIND WALK-FORWARD    IEX DAM N-REGION",
    PAGE.m, y
  );
  pdf.text(`${String(n).padStart(2, "0")} / ${String(total).padStart(2, "0")}`, PAGE.w - PAGE.m, y, { align: "right" });
}

/* ─────────── KPI card ─────────── */
export function drawKpi(pdf, x, y, w, h, { label, value, sub, hi = false, valueColor }) {
  // Card background
  pdf.setFillColor(PAPER);
  pdf.setDrawColor(hi ? LIME : LINE);
  pdf.setLineWidth(hi ? 1 : 0.5);
  pdf.roundedRect(x, y, w, h, 6, 6, "FD");

  // Accent bar on the left if highlighted
  if (hi) {
    pdf.setFillColor(LIME);
    pdf.rect(x, y, 3, h, "F");
  }

  // Label
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(MUTED);
  pdf.text(label.toUpperCase(), x + 12, y + 16);

  // Value (centered vertically by manually placing)
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.setTextColor(valueColor || (hi ? LIME : TEXT));
  pdf.text(value, x + 12, y + 44);

  // Sub
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(FAINT);
  pdf.text(sub.toUpperCase(), x + 12, y + h - 10);
}

/* ─────────── Cover page ─────────── */
function drawCover(pdf, weekDays, stats, findings, totalPages) {
  paintBg(pdf);

  // Large brand watermark (top-right). Drawn FIRST so all text sits on top.
  // Uses GState for translucency — feels like a confident publisher mark.
  pdf.setGState(new pdf.GState({ opacity: 0.07 }));
  drawLogo(pdf, PAGE.w - 200, 30, 200);
  pdf.setGState(new pdf.GState({ opacity: 1 }));

  drawHeader(pdf, `WEEKLY REPORT    ${prettyRange(weekDays[0].date, weekDays.at(-1).date)}`);

  // Title — set in two lines, "vs" italic + lime
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(34);
  pdf.setTextColor(TEXT);
  pdf.text("PowerBESS forecast", PAGE.m, 108);

  // "vs IEX DAM clearing price."
  pdf.setFont("helvetica", "italic");
  pdf.setTextColor(LIME);
  pdf.text("vs", PAGE.m, 150);
  const vsW = pdf.getTextWidth("vs");
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(TEXT);
  pdf.text(" IEX DAM clearing price.", PAGE.m + vsW, 150);

  // Subtitle
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10.5);
  pdf.setTextColor(MUTED);
  pdf.text(
    `${weekDays.length} consecutive blind day-ahead forecasts  ·  ${stats.blocks} fifteen-minute blocks  ·  gbm2 engine`,
    PAGE.m, 174
  );
  pdf.text(
    "Published before the IEX DAM auction closed. Scored against the price India actually paid.",
    PAGE.m, 190
  );

  // ─── KPI strip: 5 tiles
  const kpis = [
    { label: "Week accuracy",  value: `${fmt(stats.accuracy, 1)}%`, sub: "vol-weighted, 100 − WMAPE", hi: true },
    { label: "Week WMAPE",     value: `${fmt(stats.wmape, 2)}%`,    sub: "Σ|err| / Σ|actual|" },
    { label: "P10−P90 cover",  value: `${fmt(stats.coverage, 0)}%`, sub: "target ≥80%",
      valueColor: stats.coverage >= 80 ? LIME : AMBER },
    { label: "Mean abs error", value: `Rs ${Math.round(stats.mae)}`, sub: "per MWh, block-level" },
    { label: "Best · Worst",   value: `${fmt(stats.best._acc, 1)} · ${fmt(stats.worst._acc, 1)}%`,
      sub: `${stats.best.label.split(" ").slice(0,2).join(" ")}  ·  ${stats.worst.label.split(" ").slice(0,2).join(" ")}` },
  ];
  const gap = 10;
  const kpiW = (COL_W - gap * (kpis.length - 1)) / kpis.length;
  const kpiH = 78;
  kpis.forEach((k, i) => {
    drawKpi(pdf, PAGE.m + i * (kpiW + gap), 214, kpiW, kpiH, k);
  });

  // ─── Key findings (left)
  const findingsX = PAGE.m;
  const findingsY = 322;
  const findingsW = COL_W * 0.62;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(TEXT);
  pdf.text("Key findings", findingsX, findingsY);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(TEXT);
  let yy = findingsY + 22;
  findings.forEach((f) => {
    // Lime bullet
    pdf.setFillColor(LIME);
    pdf.circle(findingsX + 4, yy - 4, 1.8, "F");
    const lines = pdf.splitTextToSize(f, findingsW - 16);
    pdf.setTextColor(TEXT);
    pdf.text(lines, findingsX + 14, yy);
    yy += lines.length * 12 + 9;
  });

  // ─── Daily accuracy bar chart (right column)
  const chartX = PAGE.m + COL_W * 0.66;
  const chartY = findingsY;
  const chartW = COL_W * 0.34;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(13);
  pdf.setTextColor(TEXT);
  pdf.text("Daily accuracy", chartX, chartY);
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(FAINT);
  pdf.text("100 − DAILY WMAPE", chartX, chartY + 12);

  drawDailyAccuracyBars(pdf, chartX, chartY + 22, chartW, 160, stats.perDay);

  drawFooter(pdf, 1, totalPages);
}

function drawDailyAccuracyBars(pdf, x, y, w, h, perDay) {
  const n = perDay.length;
  const gap = 6;
  const barW = (w - gap * (n - 1)) / n;
  const minA = 80, maxA = 100;     // y-axis range
  const range = maxA - minA;

  // baseline + gridlines at 85, 90, 95
  pdf.setDrawColor(LINE);
  pdf.setLineWidth(0.3);
  [85, 90, 95].forEach((v) => {
    const yy = y + h - ((v - minA) / range) * h;
    pdf.line(x, yy, x + w, yy);
    pdf.setFont("courier", "normal");
    pdf.setFontSize(6);
    pdf.setTextColor(FAINT);
    pdf.text(`${v}`, x - 4, yy + 2, { align: "right" });
  });

  // bars
  perDay.forEach((d, i) => {
    const acc = Math.max(minA, Math.min(maxA, d._acc));
    const bh = ((acc - minA) / range) * h;
    const bx = x + i * (barW + gap);
    const by = y + h - bh;

    const color = d._acc >= 90 ? LIME : d._acc >= 85 ? AMBER : RED;
    pdf.setFillColor(color);
    pdf.rect(bx, by, barW, bh, "F");

    // accuracy label on top of bar
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.setTextColor(TEXT);
    pdf.text(`${fmt(d._acc, 1)}`, bx + barW / 2, by - 3, { align: "center" });

    // dow under bar
    pdf.setFont("courier", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(MUTED);
    pdf.text(d.dow.toUpperCase(), bx + barW / 2, y + h + 10, { align: "center" });
    pdf.setTextColor(FAINT);
    pdf.setFontSize(6.5);
    pdf.text(d.date.slice(-2), bx + barW / 2, y + h + 19, { align: "center" });
  });
}

/* ─────────── Day page ─────────── */
function drawDayPage(pdf, day, jpgDataUrl, stats, n, totalPages) {
  paintBg(pdf);
  drawHeader(pdf, `${day.dow.toUpperCase()}    ${prettyDate(day.date)}    BLIND DAY-AHEAD`);

  // Day title (left)
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(24);
  pdf.setTextColor(TEXT);
  pdf.text(`${day.dow}, ${prettyDate(day.date)}`, PAGE.m, 88);

  // Sub stats strip
  pdf.setFont("courier", "normal");
  pdf.setFontSize(8.5);
  pdf.setTextColor(MUTED);
  const strip = [
    `WMAPE ${fmt(day._wmape, 2)}%`,
    `MAPE ${fmt(day.mape, 1)}%`,
    `P10−P90 ${Math.round(day._cov)}%`,
    `BIAS ${fmtSigned(day._bias)} Rs/MWh`,
    `RMSE Rs ${Math.round(day._rmse)}`,
  ];
  let sx = PAGE.m;
  strip.forEach((s, i) => {
    if (i > 0) {
      pdf.setTextColor(FAINT);
      pdf.text("·", sx, 108);
      sx += pdf.getTextWidth("·") + 8;
    }
    pdf.setTextColor(MUTED);
    pdf.text(safe(s), sx, 108);
    sx += pdf.getTextWidth(safe(s)) + 8;
  });

  // Big accuracy (right) — bigger and more confident
  const accStr = fmt(day._acc, 1);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(42);
  pdf.setTextColor(day._acc >= 90 ? LIME : day._acc >= 85 ? AMBER : RED);
  pdf.text(accStr, PAGE.w - PAGE.m, 90, { align: "right" });
  const accW = pdf.getTextWidth(accStr);
  pdf.setFontSize(16);
  pdf.setTextColor(MUTED);
  pdf.text("%", PAGE.w - PAGE.m - accW + accW + 3, 90); // place % right after value
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(MUTED);
  pdf.text("ACCURACY", PAGE.w - PAGE.m, 106, { align: "right" });

  // Chart block
  if (jpgDataUrl) {
    const cx = PAGE.m;
    const cy = 130;
    const cw = COL_W;
    const ch = 320;
    pdf.setFillColor(PAPER);
    pdf.setDrawColor(LINE);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(cx, cy, cw, ch, 8, 8, "FD");
    pdf.addImage(jpgDataUrl, "JPEG", cx + 6, cy + 6, cw - 12, ch - 12, undefined, "FAST");
  }

  // Legend — under chart, centered
  drawLegend(pdf, PAGE.w / 2, 470);

  // Commentary
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(9.5);
  pdf.setTextColor(MUTED);
  const note = buildDayNote(day);
  const noteLines = pdf.splitTextToSize(note, COL_W);
  pdf.text(noteLines, PAGE.m, 500);

  drawFooter(pdf, n, totalPages);
}

function drawLegend(pdf, cx, y) {
  const items = [
    { label: "Actual IEX DAM",  type: "line",   color: TEXT },
    { label: "PowerBESS p50",   type: "dashed", color: LIME },
    { label: "P10−P90 band",    type: "swatch", color: LIME },
  ];

  // Measure total width
  pdf.setFont("courier", "normal");
  pdf.setFontSize(8);
  const totalW = items.reduce((sum, it) => sum + 26 + pdf.getTextWidth(safe(it.label)) + 24, 0) - 24;
  let lx = cx - totalW / 2;

  items.forEach((it) => {
    if (it.type === "swatch") {
      pdf.setFillColor(it.color);
      pdf.setGState(new pdf.GState({ opacity: 0.28 }));
      pdf.rect(lx, y - 6, 18, 6, "F");
      pdf.setGState(new pdf.GState({ opacity: 1 }));
      pdf.setDrawColor(it.color);
      pdf.setLineWidth(0.6);
      pdf.rect(lx, y - 6, 18, 6, "S");
    } else {
      pdf.setDrawColor(it.color);
      pdf.setLineWidth(it.type === "dashed" ? 1.8 : 1.4);
      if (it.type === "dashed") pdf.setLineDashPattern([3, 2], 0);
      pdf.line(lx, y - 3, lx + 18, y - 3);
      pdf.setLineDashPattern([], 0);
    }
    pdf.setTextColor(TEXT);
    pdf.text(safe(it.label), lx + 24, y);
    lx += 24 + pdf.getTextWidth(safe(it.label)) + 24;
  });
}

function buildDayNote(d) {
  const dir = d._bias > 0 ? "over-forecast" : "under-forecast";
  const mag = Math.abs(d._bias);
  const intensity = mag > 500 ? "noticeably" : mag > 200 ? "slightly" : "marginally";
  const cov = d._cov >= 80
    ? `Coverage ${Math.round(d._cov)}% met the 80% interval target`
    : `Coverage ${Math.round(d._cov)}% sat below the 80% interval target — the model under-stated its own uncertainty`;
  if (d._acc >= 90) {
    return `Strong day. The model tracked both the off-peak floor and the evening peak, ${intensity} ${dir} by Rs ${Math.round(mag)}/MWh on the bias channel. ${cov}.`;
  }
  if (d._acc >= 85) {
    return `Solid day with localised misses. ${intensity[0].toUpperCase() + intensity.slice(1)} ${dir} of Rs ${Math.round(mag)}/MWh, typically concentrated in the evening peak window. ${cov}.`;
  }
  return `Tougher day. ${intensity[0].toUpperCase() + intensity.slice(1)} ${dir} of Rs ${Math.round(mag)}/MWh suggests a regime shift the model only partially anticipated. ${cov}.`;
}

/* ─────────── Comparison table page ─────────── */
function drawTablePage(pdf, weekDays, stats, n, totalPages) {
  paintBg(pdf);
  drawHeader(pdf, "WEEK AT A GLANCE");

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(24);
  pdf.setTextColor(TEXT);
  pdf.text("Day-by-day comparison", PAGE.m, 88);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(MUTED);
  pdf.text(
    "Volume-weighted accuracy bands:  ≥90% lime  ·  85–90% amber  ·  <85% red.",
    PAGE.m, 108
  );

  // Table layout: 7 cols
  const headers = ["DATE", "ACCURACY", "WMAPE", "MAPE", "MAE  Rs/MWh", "P10−P90", "BIAS  Rs"];
  const colW =    [ 150,     90,         80,      80,        100,         80,         82  ];
  const totalW = colW.reduce((a, b) => a + b, 0);
  const tx = PAGE.m + (COL_W - totalW) / 2;
  const headerY = 144;
  const rowH = 28;

  // Header bg
  pdf.setFillColor(PAPER);
  pdf.rect(tx, headerY - 18, totalW, 26, "F");
  pdf.setDrawColor(LIME);
  pdf.setLineWidth(0.7);
  pdf.line(tx, headerY + 8, tx + totalW, headerY + 8);

  // Header text
  pdf.setFont("courier", "normal");
  pdf.setFontSize(7.5);
  pdf.setTextColor(MUTED);
  let cx = tx;
  headers.forEach((h, i) => {
    pdf.text(safe(h), cx + (i === 0 ? 12 : colW[i] - 8), headerY, { align: i === 0 ? "left" : "right" });
    cx += colW[i];
  });

  // Rows
  pdf.setFontSize(10.5);
  weekDays.forEach((d, i) => {
    const ry = headerY + 24 + i * rowH;
    // alternating zebra
    if (i % 2 === 1) {
      pdf.setFillColor(15, 15, 17);
      pdf.rect(tx, ry - 14, totalW, rowH, "F");
    }
    pdf.setDrawColor(LINE);
    pdf.setLineWidth(0.3);
    pdf.line(tx, ry + 12, tx + totalW, ry + 12);

    let x = tx;
    // Date col with bold dow + light date
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(TEXT);
    pdf.text(d.dow, x + 12, ry);
    const dowW = pdf.getTextWidth(d.dow);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(MUTED);
    pdf.text(`  ${prettyDate(d.date).split(" ").slice(0,2).join(" ")}`, x + 12 + dowW, ry);
    x += colW[0];

    // Accuracy — color-coded
    const accColor = d._acc >= 90 ? LIME : d._acc >= 85 ? AMBER : RED;
    pdf.setTextColor(accColor);
    pdf.setFont("helvetica", "bold");
    pdf.text(`${fmt(d._acc, 1)}%`, x + colW[1] - 8, ry, { align: "right" });
    x += colW[1];

    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(TEXT);
    pdf.text(`${fmt(d._wmape, 2)}%`, x + colW[2] - 8, ry, { align: "right" });
    x += colW[2];

    pdf.text(`${fmt(d.mape, 1)}%`, x + colW[3] - 8, ry, { align: "right" });
    x += colW[3];

    pdf.text(`${Math.round(d._mae)}`, x + colW[4] - 8, ry, { align: "right" });
    x += colW[4];

    pdf.setTextColor(d._cov >= 80 ? LIME : AMBER);
    pdf.text(`${Math.round(d._cov)}%`, x + colW[5] - 8, ry, { align: "right" });
    x += colW[5];

    pdf.setTextColor(d._bias >= 0 ? BLUE : "#FFB35A");
    pdf.text(`${fmtSigned(d._bias)}`, x + colW[6] - 8, ry, { align: "right" });
  });

  // ─── Total row
  const tot_y = headerY + 24 + weekDays.length * rowH + 4;
  pdf.setDrawColor(LIME);
  pdf.setLineWidth(0.8);
  pdf.line(tx, tot_y - 12, tx + totalW, tot_y - 12);

  pdf.setFont("courier", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(MUTED);
  pdf.text("WEEK TOTAL", tx + 12, tot_y);

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10.5);
  let xt = tx + colW[0];
  pdf.setTextColor(stats.accuracy >= 90 ? LIME : stats.accuracy >= 85 ? AMBER : RED);
  pdf.text(`${fmt(stats.accuracy, 1)}%`, xt + colW[1] - 8, tot_y, { align: "right" });
  xt += colW[1];
  pdf.setTextColor(TEXT);
  pdf.text(`${fmt(stats.wmape, 2)}%`, xt + colW[2] - 8, tot_y, { align: "right" });
  xt += colW[2];
  pdf.setTextColor(MUTED);
  pdf.text("—", xt + colW[3] - 8, tot_y, { align: "right" });
  xt += colW[3];
  pdf.setTextColor(TEXT);
  pdf.text(`${Math.round(stats.mae)}`, xt + colW[4] - 8, tot_y, { align: "right" });
  xt += colW[4];
  pdf.setTextColor(stats.coverage >= 80 ? LIME : AMBER);
  pdf.text(`${Math.round(stats.coverage)}%`, xt + colW[5] - 8, tot_y, { align: "right" });
  xt += colW[5];
  pdf.setTextColor(stats.bias >= 0 ? BLUE : "#FFB35A");
  pdf.text(`${fmtSigned(stats.bias)}`, xt + colW[6] - 8, tot_y, { align: "right" });

  // Footnote
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8.5);
  pdf.setTextColor(MUTED);
  const footY = PAGE.h - PAGE.m - 64;
  pdf.text(
    "Accuracy  =  100 − WMAPE.  WMAPE  =  Σ |forecast − actual|  ÷  Σ |actual|,  volume-weighted across all blocks.",
    PAGE.m, footY
  );
  pdf.text(
    "P10−P90  =  share of blocks whose realised price fell within the model's 10th–90th percentile band.   Target ≥ 80%.",
    PAGE.m, footY + 14
  );
  pdf.text(
    "Bias  =  mean (forecast − actual) in Rs/MWh.   Positive = over-forecast,  negative = under-forecast.",
    PAGE.m, footY + 28
  );

  drawFooter(pdf, n, totalPages);
}

/* ─────────── Public entry ─────────── */
export async function exportWeeklyPDF({ forecastData, days, mountNode, onProgress }) {
  const weekDays = pickLast7(days);
  const stats = aggregate(weekDays, forecastData);
  const findings = buildFindings(weekDays, stats);
  const totalPages = 1 + weekDays.length + 1;     // cover + N days + comparison

  // 1) Render each chart off-screen → JPEG
  const charts = [];
  for (let i = 0; i < weekDays.length; i++) {
    const d = weekDays[i];
    const rows = forecastData[d.date] || [];
    onProgress?.(((i + 1) / weekDays.length) * 70, `Rendering ${d.label}…`);
    const jpg = await renderChartToImg(rows, mountNode);
    charts.push(jpg);
  }

  // 2) Build PDF
  onProgress?.(75, "Composing PDF…");
  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

  // Monkey-patch text/splitTextToSize so unicode glyphs are always converted
  const _text = pdf.text.bind(pdf);
  pdf.text = (txt, ...rest) => {
    const converted = Array.isArray(txt) ? txt.map(safe) : safe(txt);
    return _text(converted, ...rest);
  };
  const _split = pdf.splitTextToSize.bind(pdf);
  pdf.splitTextToSize = (txt, w) => _split(safe(txt), w);

  drawCover(pdf, weekDays, stats, findings, totalPages);

  stats.perDay.forEach((d, i) => {
    pdf.addPage("a4", "landscape");
    drawDayPage(pdf, d, charts[i], stats, i + 2, totalPages);
  });

  pdf.addPage("a4", "landscape");
  drawTablePage(pdf, stats.perDay, stats, totalPages, totalPages);

  pdf.setProperties({
    title: `PowerBESS Weekly Forecast vs DAM — ${prettyRange(weekDays[0].date, weekDays.at(-1).date)}`,
    subject: "Blind day-ahead forecast accuracy",
    author: "PowerBESS.ai",
    keywords: "IEX DAM, PowerBESS, forecast, accuracy, WMAPE",
    creator: "PowerBESS.ai",
  });

  onProgress?.(100, "Done");
  const fileName = `PowerBESS_Weekly_${yyyymmdd(weekDays[0].date)}-${yyyymmdd(weekDays.at(-1).date)}.pdf`;
  return { pdf, fileName };
}
